import type {
  BaseQueryFn,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'
import { retry, createApi } from '@reduxjs/toolkit/query/react'
import type { AxiosError, AxiosRequestConfig } from 'axios'
import { customAxios, isAxiosError } from '@/utils/axiosHelper'
import { ServiceErrorHandler } from '@/connect-types/backend/service'
import config from '@/config/config.common'
import type {
  NetXLProductsResponse,
  NetXLProductResponse,
  NetXLProductStockResponse,
  NetXLOrdersResponse,
  NetXLOrderResponse,
  NetXLCreateOrderRequest,
  NetXLCreateOrderResponse,
  NetXLLinkDeviceRequest,
  NetXLHealth,
  NetXLAccount,
  NetXLAddressesResponse,
  NetXLProductsQueryParams,
  NetXLOrdersQueryParams,
  NetXLCreatePaymentLinkRequest,
  NetXLCreatePaymentLinkResponse,
} from './netxl.types'

const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: '' }
  ): BaseQueryFn<
    {
      url: string
      method: AxiosRequestConfig['method']
      data?: AxiosRequestConfig['data']
      params?: AxiosRequestConfig['params']
      headers?: AxiosRequestConfig['headers']
    },
    unknown,
    FetchBaseQueryError
  > =>
  async ({ url, method, data, params, headers }) => {
    try {
      const result = await customAxios({
        url: baseUrl + url,
        method,
        data,
        params,
        headers,
      })
      return { data: result.data }
    } catch (axiosError) {
      const err = axiosError as AxiosError

      if (isAxiosError(err)) {
        const message = err.response?.data
          ? ((err.response.data as Record<string, unknown>).error ??
            (err.response.data as Record<string, unknown>).message ??
            'Request failed')
          : err.message

        ServiceErrorHandler(
          err?.response?.status ?? 0,
          'NetXL API Error',
          String(message),
          () => undefined
        )

        return {
          error: {
            status: err.response?.status,
            data: err.response?.data ?? err.message,
          },
        }
      }

      return {
        error: {
          status: 500,
          data: 'An unexpected error occurred',
        },
      }
    }
  }

const netxlApi = createApi({
  reducerPath: 'netxl',
  keepUnusedDataFor: 60 * 5, // Cache for 5 minutes
  baseQuery: retry(
    axiosBaseQuery({ baseUrl: `${config.url.trinity}/api/netxl` }),
    {
      maxRetries: 2,
    }
  ),
  tagTypes: [
    'NetXLProducts',
    'NetXLOrders',
    'NetXLHealth',
    'NetXLAccount',
    'NetXLAddresses',
  ],
  endpoints: (builder) => ({
    // Health check
    getNetXLHealth: builder.query<NetXLHealth, void>({
      query: () => ({
        url: '/health',
        method: 'GET',
      }),
      providesTags: ['NetXLHealth'],
    }),

    // Products
    getProducts: builder.query<NetXLProductsResponse, NetXLProductsQueryParams>(
      {
        query: (params) => ({
          url: '/products',
          method: 'GET',
          params,
        }),
        providesTags: (result) =>
          result?.data
            ? [
                ...result.data.map(({ id }) => ({
                  type: 'NetXLProducts' as const,
                  id,
                })),
                { type: 'NetXLProducts', id: 'LIST' },
              ]
            : [{ type: 'NetXLProducts', id: 'LIST' }],
      }
    ),

    getProduct: builder.query<NetXLProductResponse, string>({
      query: (productId) => ({
        url: `/products/${productId}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'NetXLProducts', id }],
    }),

    getProductStock: builder.query<NetXLProductStockResponse, string>({
      query: (productId) => ({
        url: `/products/${productId}/stock`,
        method: 'GET',
      }),
    }),

    // Orders
    getOrders: builder.query<NetXLOrdersResponse, NetXLOrdersQueryParams>({
      query: (params) => ({
        url: '/orders',
        method: 'GET',
        params,
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({
                type: 'NetXLOrders' as const,
                id,
              })),
              { type: 'NetXLOrders', id: 'LIST' },
            ]
          : [{ type: 'NetXLOrders', id: 'LIST' }],
    }),

    getOrder: builder.query<NetXLOrderResponse, string>({
      query: (orderId) => ({
        url: `/orders/${orderId}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'NetXLOrders', id }],
    }),

    getOrderByDeviceSerial: builder.query<NetXLOrderResponse, string>({
      query: (deviceSerial) => ({
        url: `/orders/by-device/${deviceSerial}`,
        method: 'GET',
      }),
    }),

    createOrder: builder.mutation<
      NetXLCreateOrderResponse,
      NetXLCreateOrderRequest
    >({
      query: (data) => ({
        url: '/orders',
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'NetXLOrders', id: 'LIST' }],
    }),

    syncOrder: builder.mutation<{ success: boolean; message: string }, string>({
      query: (orderId) => ({
        url: `/orders/${orderId}/sync`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'NetXLOrders', id }],
    }),

    syncAllOrders: builder.mutation<
      { success: boolean; message: string },
      void
    >({
      query: () => ({
        url: '/orders/sync-all',
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'NetXLOrders', id: 'LIST' }],
    }),

    linkDevice: builder.mutation<
      { success: boolean; message: string },
      { orderId: string } & NetXLLinkDeviceRequest
    >({
      query: ({ orderId, ...data }) => ({
        url: `/orders/${orderId}/link-device`,
        method: 'POST',
        data,
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: 'NetXLOrders', id: orderId },
      ],
    }),

    // Account
    getAccount: builder.query<NetXLAccount, void>({
      query: () => ({
        url: '/account',
        method: 'GET',
      }),
      providesTags: ['NetXLAccount'],
    }),

    // Addresses
    getAddresses: builder.query<NetXLAddressesResponse, void>({
      query: () => ({
        url: '/addresses',
        method: 'GET',
      }),
      providesTags: ['NetXLAddresses'],
    }),

    // Payment Links
    createPaymentLink: builder.mutation<
      NetXLCreatePaymentLinkResponse,
      NetXLCreatePaymentLinkRequest
    >({
      query: (data) => ({
        url: '/payment-link',
        method: 'POST',
        data,
      }),
    }),
  }),
})

export const {
  useGetNetXLHealthQuery,
  useGetProductsQuery,
  useGetProductQuery,
  useGetProductStockQuery,
  useGetOrdersQuery,
  useGetOrderQuery,
  useGetOrderByDeviceSerialQuery,
  useCreateOrderMutation,
  useSyncOrderMutation,
  useSyncAllOrdersMutation,
  useLinkDeviceMutation,
  useGetAccountQuery,
  useGetAddressesQuery,
  useCreatePaymentLinkMutation,
} = netxlApi

export default netxlApi
